// The live route line on the rider's trip screen (ActiveRide): Anna -> pickup, then auto -> drop.
// Kept free of React, Firebase and the map so it can be tested on its own.
//
// Industry behaviour (Uber, Ola, Rapido): the line starts at the auto and shrinks as he drives; the
// part already driven disappears. That is plain geometry on the route we already have, so it costs
// nothing. Only when he really leaves the route (a wrong turn, a U-turn, another road) is a new
// route fetched from where he is, and that is a paid Directions call, so it is rate-limited and
// capped per leg (Maps is most of the bill).

/** Further than this from the remaining route counts as off it (GPS here is rounded to ~11 m). */
export const OFF_ROUTE_M = 60;
/** Off the route on this many GPS updates in a row before re-routing: one bad fix never does. */
export const OFF_ROUTE_FIXES = 2;
/** At most one re-route per this long. */
export const REROUTE_COOLDOWN_MS = 30 * 1000;
/** At most this many re-routes per leg (the first route of a leg is not counted). */
export const MAX_REROUTES_PER_LEG = 5;
/** The line is redrawn only when its start has moved at least this far. */
export const REDRAW_MIN_MOVE_M = 10;
/** A later part of the route this much nearer than an earlier one is not enough to skip ahead. */
const LOOP_TOLERANCE_M = 10;

const EARTH_M = 6371000;
const rad = (d) => (d * Math.PI) / 180;

/** `{lat, lng}` from a plain point or a google.maps.LatLng; null when unusable. */
export const toPoint = (p) => {
  if (!p) return null;
  const lat = typeof p.lat === 'function' ? p.lat() : Number(p.lat);
  const lng = typeof p.lng === 'function' ? p.lng() : Number(p.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

export const toPath = (path) => (Array.isArray(path) ? path : []).map(toPoint).filter(Boolean);

/** Metres between two points (flat-earth: plenty at town scale). */
export const metresBetween = (a, b) => {
  const k = Math.cos(rad((a.lat + b.lat) / 2));
  const dx = rad(b.lng - a.lng) * EARTH_M * k;
  const dy = rad(b.lat - a.lat) * EARTH_M;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Where the auto is on the route: the nearest point of the route still ahead of `from` (the last
 * accepted progress, `{segment, t}`), so progress only moves forward and a road the route uses
 * twice (out and back) is never skipped ahead on. Returns `{segment, t, point, offM}` (`offM` =
 * metres from the auto to that point), or null without a usable route or position.
 */
export const projectOnRoute = (path, at, from = null) => {
  const pts = toPath(path);
  const p = toPoint(at);
  if (pts.length < 2 || !p) return null;
  const k = Math.cos(rad(p.lat));
  const local = (q) => ({ x: rad(q.lng - p.lng) * EARTH_M * k, y: rad(q.lat - p.lat) * EARTH_M });
  const start = Math.min(Math.max(0, Number(from?.segment) || 0), pts.length - 2);
  const minT = Math.min(Math.max(0, Number(from?.t) || 0), 1);
  const found = [];
  for (let i = start; i < pts.length - 1; i += 1) {
    const a = local(pts[i]);
    const b = local(pts[i + 1]);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 0 ? -(a.x * dx + a.y * dy) / len2 : 0;
    t = Math.min(Math.max(t, i === start ? minT : 0), 1);
    const cx = a.x + t * dx;
    const cy = a.y + t * dy;
    found.push({ segment: i, t, d: Math.sqrt(cx * cx + cy * cy) });
  }
  // The earliest stretch that is about as near as the nearest one, then the closest point along it
  // (the corner where two pieces of one stretch meet is never nearer than the road between them).
  const minD = Math.min(...found.map((f) => f.d));
  let j = found.findIndex((f) => f.d <= minD + LOOP_TOLERANCE_M);
  while (j + 1 < found.length && found[j + 1].d <= found[j].d) j += 1;
  const best = found[j];
  const a = pts[best.segment];
  const b = pts[best.segment + 1];
  return {
    segment: best.segment,
    t: best.t,
    point: { lat: a.lat + (b.lat - a.lat) * best.t, lng: a.lng + (b.lng - a.lng) * best.t },
    offM: best.d,
  };
};

/** The route still to drive, starting at the projected point. Empty once nothing is left. */
export const remainingRoute = (path, projection) => {
  const pts = toPath(path);
  if (!projection || pts.length < 2) return [];
  const rest = pts.slice(projection.segment + 1);
  if (rest.length && metresBetween(rest[0], projection.point) < 0.5) rest.shift();
  const out = [projection.point, ...rest];
  return out.length >= 2 ? out : [];
};

/** Whether a new route may be fetched now (cooldown and per-leg cap). */
export const canReroute = (leg, now) => (Number(leg?.reroutes) || 0) < MAX_REROUTES_PER_LEG
  && now - (Number(leg?.lastRerouteAt) || 0) >= REROUTE_COOLDOWN_MS;

/**
 * One GPS update's verdict: `offFixes` is the new run of off-route updates, `reroute` says to fetch
 * a new route from the auto now.
 */
export const offRouteStep = (leg, offM, now) => {
  if (!(Number(offM) > OFF_ROUTE_M)) return { offFixes: 0, reroute: false };
  const offFixes = (Number(leg?.offFixes) || 0) + 1;
  const reroute = offFixes >= OFF_ROUTE_FIXES && canReroute(leg, now);
  return { offFixes: reroute ? 0 : offFixes, reroute };
};
